import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useApi } from '@/shared/composables/api'
import { useUserData } from '@/shared/composables/local-storage'
import { TIMEZONES } from '@/shared/lib/timezones'
import { Configuration } from '@/shared/types/api'
import { UserRoles } from '@/shared/types/router'
import { appBaseUrl, ensureAppBaseUrlReady, tauriMode } from '@/shared/utils/import-meta'
import { resolveSipWebSocketURL } from '@/shared/utils/url'

// Kick Tauri DOMAINADRESS read early (side effect only — do not freeze SIP default here).
if (tauriMode()) {
  appBaseUrl()
}

/** Keep raw API value (or empty). Absolute WS URL is resolved lazily in sipWs computed. */
const rawSipWs = (value: string | null | undefined) => value?.trim() ?? ''

export const useStoreConfiguration = defineStore('configuration', () => {
  const configuration = ref<Configuration>({
    isWebClientFilesEnabled: true,
    sipWs: '',
    tzOffset: 180,
  })

  const { userRole } = useUserData()

  /** URL для SIP WebSocket: зависит от роли (гость → /gsip, иначе /sip). Реактивное свойство — после авторизации гостя без F5 подставляется /gsip. */
  const sipWs = computed(() => {
    const fromApi = configuration.value.sipWs
    const isGuestRole = userRole.value === UserRoles.GUEST
    if (isGuestRole) {
      const normalized = resolveSipWebSocketURL(fromApi, '/gsip')
      return normalized.includes('gsip') ? normalized : resolveSipWebSocketURL(undefined, '/gsip')
    }
    return resolveSipWebSocketURL(fromApi, '/sip')
  })

  const domainPath = computed(() => configuration.value.domainPath ?? 'ROOT')
  const filesEnabled = computed(() => configuration.value.isWebClientFilesEnabled)

  const load = async (): Promise<Configuration> => {
    if (tauriMode()) {
      await ensureAppBaseUrlReady()
    }
    try {
      const api = useApi()
      const { data } = await api.getConfiguration()
      configuration.value = { ...configuration.value, ...data, sipWs: rawSipWs(data.sipWs) }
      return data
    } catch {
    }
    return configuration.value
  }

  const loadGuest = async (): Promise<Configuration> => {
    if (tauriMode()) {
      await ensureAppBaseUrlReady()
    }
    try {
      const api = useApi()
      const { data } = await api.getConfigurationGuest()
      configuration.value = { ...configuration.value, ...data, sipWs: rawSipWs(data.sipWs) }
      return data
    } catch {
    }
    return {
      isWebClientFilesEnabled: true,
      sipWs: '',
      tzOffset: 180,
    }
  }

  const timezoneServer = computed(() => {
    const totalMinutes = configuration.value.tzOffset
    const hours = Math.floor(totalMinutes / 60)
    const minutes = Math.abs(totalMinutes % 60)
    const sign = totalMinutes >= 0 ? '+' : '-'
    const timezoneStr = `${sign}${String(Math.abs(hours)).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    return TIMEZONES.find(timezone => timezone.label === timezoneStr)?.value
  })

  return {
    configuration,
    domainPath,
    filesEnabled,
    load,
    loadGuest,
    sipWs,
    timezoneServer,
  }
})
